import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  fromPromise,
  split,
} from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { createClient } from "graphql-ws";
import { getMainDefinition } from "@apollo/client/utilities";

// Dynamic environment resolution for browser runtimes
const GATEWAY_URL = import.meta.env.VITE_GATEWAY_URL || "http://localhost:4000";
const WS_GATEWAY_URL = import.meta.env.VITE_WS_GATEWAY_URL || "ws://localhost:4000/graphql";

interface GraphQLErrorExtensions {
  code?: string;
  statusCode?: number;
}

export const refreshToken = async (): Promise<boolean> => {
  try {
    // Dynamic URL replacement
    const response = await fetch(`${GATEWAY_URL}/refresh-token`, {
      method: "GET",
      credentials: "include",
    });

    if (response.ok) return true;

    console.error("Token refresh failed:", response.status);
    return false;
  } catch (error) {
    console.error("Token refresh error:", error);
    return false;
  }
};

/** =========================
 * Error link (HTTP only) + refresh/retry
 * ========================= */
const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      graphQLErrors.forEach((error) => {
        console.error("[GraphQL error]:", {
          message: error.message,
          extensions: error.extensions,
          path: error.path,
        });
      });
    }

    if (networkError) console.error("[Network error]:", networkError);

    const context = operation.getContext();
    if (context.skipAuth) return;

    const hasAuthError = graphQLErrors?.some((error) => {
      const extensions = error.extensions as GraphQLErrorExtensions;
      const code = extensions?.code;
      const statusCode = extensions?.statusCode;
      
      return code === "UNAUTHENTICATED" || statusCode === 401;
    });

    if (!hasAuthError) return;

    return fromPromise(
      refreshToken().then((ok) => {
        if (!ok) throw new Error("Refresh failed");
        return true;
      }),
    ).flatMap(() => forward(operation));
  },
);

/** =========================
 * HTTP link
 * ========================= */
const httpLink = new HttpLink({
  uri: `${GATEWAY_URL}/graphql`, // Dynamic URL replacement
  credentials: "include",
});

/** =========================
 * WS link (Subscriptions)
 * ========================= */
const wsLink = new GraphQLWsLink(
  createClient({
    url: WS_GATEWAY_URL, // Dynamic URL replacement
    lazy: true,
    retryAttempts: 10,
    connectionParams: async () => ({}),
  }),
);

/** =========================
 * Split: subscription => WS, else => HTTP chain
 * ========================= */
const httpChain = ApolloLink.from([errorLink, httpLink]);

const splitLink = split(
  ({ query }) => {
    const def = getMainDefinition(query);
    return (
      def.kind === "OperationDefinition" && def.operation === "subscription"
    );
  },
  wsLink,
  httpChain,
);

export const client = new ApolloClient({
  link: splitLink,
  cache: new InMemoryCache(),
  defaultOptions: {
    watchQuery: { errorPolicy: "all" },
    query: { errorPolicy: "all" },
    mutate: { errorPolicy: "all" },
  },
});